/**
 * Buybox-2 Cart Handler
 * Handles Shopify cart API integration and selling plan application
 * Based on buybox-1-cart-handler.js but adapted for buybox-2
 */

const Buybox2CartHandler = {
  countSelectedFlavors(flavors) {
    let count = 0;
    Object.values(flavors || {}).forEach((flavor) => {
      const qty = parseInt(flavor && flavor.quantity, 10) || 0;
      if (flavor && flavor.selected && qty > 0) count += 1;
    });
    return count;
  },

  cartLineSellingPlanId(line) {
    if (!line) return '';
    const fromAlloc = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan
      ? line.selling_plan_allocation.selling_plan.id
      : null;
    if (fromAlloc != null && String(fromAlloc).trim()) return String(fromAlloc).trim();
    if (line.selling_plan != null && String(line.selling_plan).trim()) {
      return String(line.selling_plan).trim();
    }
    return '';
  },

  guideVariantIdFromConfig(freebieConfig) {
    return parseInt(String((freebieConfig && freebieConfig.guideVariantId) || ''), 10) || 0;
  },

  scoopVariantIdFromConfig(freebieConfig) {
    return parseInt(String((freebieConfig && freebieConfig.scoopVariantId) || ''), 10) || 0;
  },

  digitalGuideConfig(freebieConfig) {
    const sellingPlanId = String((freebieConfig && freebieConfig.digitalSellingPlanId) || '').trim();
    const guideVariantId = this.guideVariantIdFromConfig(freebieConfig);
    if (!sellingPlanId || !guideVariantId) return null;
    return { sellingPlanId, guideVariantId };
  },

  rewardsExperimentOn(freebieConfig) {
    if (freebieConfig && freebieConfig.rewardsOn === true) return true;
    if (freebieConfig && freebieConfig.rewardsOn === false) return false;
    if (typeof window !== 'undefined' && typeof window.bzRewardsExperimentOn === 'function') {
      try {
        return !!window.bzRewardsExperimentOn();
      } catch (e) {}
    }
    return false;
  },

  /**
   * Legacy Recurring-subtotal hide: Transitiegids + Digital when exactly 1 flavor.
   * Used only when rewards_v1 is off.
   */
  shouldAddDigitalGuide(state, freebieConfig) {
    if (!state || state.deliveryMode !== 'subscription') return false;
    if (this.countSelectedFlavors(state.flavors) !== 1) return false;
    return Boolean(this.digitalGuideConfig(freebieConfig));
  },

  async fetchCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) return { items: [] };
    return response.json();
  },

  async changeCartLine(opts) {
    const key = String((opts && (opts.key || opts.id)) || '').trim();
    if (!key) throw new Error('changeCartLine requires a line key');
    const body = {
      id: key,
      quantity: opts && opts.quantity != null ? opts.quantity : 1
    };
    if (opts && opts.clearSellingPlan === true) {
      body.selling_plan = null;
    } else if (opts && opts.sellingPlanId) {
      body.selling_plan = opts.sellingPlanId;
    }
    if (opts && opts.properties && typeof opts.properties === 'object') {
      body.properties = opts.properties;
    }
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || errorData.description || 'Cart change failed';
      throw new Error(errorMessage);
    }
    return response.json();
  },

  acquisitionProps(state) {
    const acquisitionFlow = String((state && state.acquisitionFlowId) || 'buybox-2');
    return { _acquisition_flow: acquisitionFlow };
  },

  async ensureVariantLine(state, variantId, sellingPlanId) {
    const vid = parseInt(String(variantId || ''), 10) || 0;
    if (!vid) return;
    const properties = this.acquisitionProps(state);
    const planId = String(sellingPlanId || '').trim();

    let cart = { items: [] };
    try {
      cart = await this.fetchCart();
    } catch (e) {
      cart = { items: [] };
    }

    const existingLine = (cart.items || []).find(
      (it) => parseInt(String(it.variant_id), 10) === vid
    );

    if (!existingLine) {
      const item = { id: vid, quantity: 1, properties };
      if (planId) item.selling_plan = planId;
      await this.addToCart([item]);
      return;
    }

    const existingPlan = this.cartLineSellingPlanId(existingLine);
    const wantPlan = planId;
    const needPlanChange = wantPlan
      ? existingPlan !== wantPlan
      : Boolean(existingPlan);
    if (!needPlanChange) return;

    await this.changeCartLine({
      key: existingLine.key,
      quantity: existingLine.quantity || 1,
      sellingPlanId: wantPlan || undefined,
      clearSellingPlan: !wantPlan,
      properties: Object.assign({}, existingLine.properties || {}, properties)
    });
  },

  /**
   * One-flavor subscribe (rewards off): Transitiegids + Digital plan so Shopify hides Recurring subtotal.
   */
  async ensureDigitalGuideForSingleFlavor(state, freebieConfig) {
    if (!this.shouldAddDigitalGuide(state, freebieConfig)) return;
    const guide = this.digitalGuideConfig(freebieConfig);
    await this.ensureVariantLine(state, guide.guideVariantId, guide.sellingPlanId);
  },

  /**
   * rewards_v1:on + subscribe: always scoop (one-time) + guide.
   * Guide uses Digital only for 1 flavor (Recurring hide); 2+ flavors = one-time guide.
   */
  async ensureRewardsBox1Freebies(state, freebieConfig) {
    if (!state || state.deliveryMode !== 'subscription') return;
    if (!this.rewardsExperimentOn(freebieConfig)) return;

    const scoopId = this.scoopVariantIdFromConfig(freebieConfig);
    if (scoopId) {
      await this.ensureVariantLine(state, scoopId, '');
    }

    const guideId = this.guideVariantIdFromConfig(freebieConfig);
    if (!guideId) return;

    const flavorCount = this.countSelectedFlavors(state.flavors);
    const digital = this.digitalGuideConfig(freebieConfig);
    const planId = flavorCount === 1 && digital ? digital.sellingPlanId : '';
    await this.ensureVariantLine(state, guideId, planId);
  },

  /**
   * One-time checkout must not keep Digital on the Transitiegids (leftover from
   * a prior subscribe add). That single selling plan shows Recurring subtotal.
   */
  async stripDigitalGuideForOnetime(state, freebieConfig) {
    if (!state || state.deliveryMode !== 'onetime') return;
    const guideVariantId = this.guideVariantIdFromConfig(freebieConfig);
    if (!guideVariantId) return;

    let cart = { items: [] };
    try {
      cart = await this.fetchCart();
    } catch (e) {
      return;
    }

    const existingLine = (cart.items || []).find(
      (it) => parseInt(String(it.variant_id), 10) === guideVariantId
    );
    if (!existingLine) return;
    if (!this.cartLineSellingPlanId(existingLine)) return;

    await this.changeCartLine({
      key: existingLine.key,
      quantity: existingLine.quantity || 1,
      clearSellingPlan: true
    });
  },

  /**
   * Build cart items payload for buybox-2
   * @param {Object} state - Current modal state
   * @param {Object} productConfig - Product configuration (variants, etc.)
   * @param {Function} getSellingPlanId - Function to get selling plan ID for days
   * @returns {Promise<Array>} Array of cart items
   */
  async buildCartItems(state, productConfig, getSellingPlanId) {
    const items = [];
    
    try {
      const { flavors, deliveryMode, selectedBulkOption } = state;
      
      // Validate delivery mode and selling plan for subscription
      if (deliveryMode === 'subscription') {
        const baseDays = parseInt(state.sellingPlan);
        if (!baseDays || (baseDays !== 28 && baseDays !== 56 && baseDays !== 84)) {
          // Invalid selling plan - will default to 28 days in calculation
        }
      }
      
      // Add main flavor variants
      FlavorDistribution.forEachFlavorKeyInOrder(flavors, (flavorKey, flavor) => {
        if (!flavor.selected || flavor.quantity === 0) return;
        
        const variantId = productConfig.variants[flavorKey];
        if (!variantId) {
          return;
        }
        
        // Calculate quantity with bulk multiplier
        const quantity = flavor.quantity * selectedBulkOption;
        
        const item = {
          id: variantId,
          quantity: quantity,
          properties: {
            _acquisition_flow: productConfig.acquisitionFlow || 'buybox-2'
          }
        };
        
        // Add selling plan if subscription mode
        if (deliveryMode === 'subscription') {
          const baseDays = parseInt(state.sellingPlan) || 28;
          const totalDays = baseDays * selectedBulkOption;
          
          const sellingPlanId = getSellingPlanId(totalDays);
          
          if (sellingPlanId) {
            item.selling_plan = sellingPlanId;
          } else {
            // This should never happen if validation in setupAddToCartButton worked correctly
            // But add extra safety check here
            const errorMsg = `No selling plan found for ${totalDays} days. Variant: ${variantId}, baseDays: ${baseDays}, bulkOption: ${selectedBulkOption}. Please ensure selling plans are loaded.`;
            throw new Error(errorMsg);
          }
        }
        
        items.push(item);
      });
      
      if (items.length === 0) {
        throw new Error('No items to add to cart');
      }
      
      return items;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Add items to cart via Shopify API (JSON format, like preorder-modal-3)
   * @param {Array} items - Array of cart items
   * @returns {Promise<Object>} Cart response
   */
  async applyDiscountCode(code) {
    const next = String(code || '').trim();
    if (!next) return;
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
    const existing = (cart.discount_codes || []).map((row) => row && row.code).filter(Boolean);
    if (existing.some((row) => row.toLowerCase() === next.toLowerCase())) return;
    existing.push(next);
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: existing.join(',') })
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.description || errorData.message || 'Discount code failed');
    }
  },

  async addToCart(items) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || errorData.description || 'Cart addition failed';
        throw new Error(`Failed to add items to cart: ${errorMessage}`);
      }
      
      const result = await response.json();
      if (typeof window.bzStampVwoExperiments === 'function') {
        try {
          await window.bzStampVwoExperiments();
        } catch (e) {}
      }
      if (typeof window.bzStampExperiments === 'function') {
        try {
          await window.bzStampExperiments();
        } catch (e) {}
      }
      return result;
    } catch (error) {
      throw error;
    }
  },
  
  /**
   * Process complete cart addition flow
   * @param {Object} state - Current modal state
   * @param {Object} productConfig - Product configuration
   * @param {Function} getSellingPlanId - Function to get selling plan ID for days
   * @param {Object} [freebieConfig] - Digital plan + guide/scoop variants + rewardsOn
   * @returns {Promise<boolean>} Success status
   */
  async processCartAddition(state, productConfig, getSellingPlanId, freebieConfig) {
    try {
      // Build cart items
      const items = await this.buildCartItems(state, productConfig, getSellingPlanId);
      
      if (items.length === 0) {
        throw new Error('No items to add to cart');
      }
      
      // Add to cart
      await this.addToCart(items);

      const discountCodes = productConfig
        ? [productConfig.beefDiscountCode, productConfig.subscriptionDiscountCode]
        : [];
      for (let i = 0; i < discountCodes.length; i++) {
        if (!discountCodes[i]) continue;
        try {
          await this.applyDiscountCode(discountCodes[i]);
        } catch (discountError) {
          console.warn('Cart discount was not applied', discountCodes[i], discountError);
        }
      }

      if (this.rewardsExperimentOn(freebieConfig)) {
        await this.ensureRewardsBox1Freebies(state, freebieConfig);
      } else {
        await this.ensureDigitalGuideForSingleFlavor(state, freebieConfig);
      }
      await this.stripDigitalGuideForOnetime(state, freebieConfig);
      
      return true;
    } catch (error) {
      throw error;
    }
  }
};

// Export for use in buybox-2.js
if (typeof module !== 'undefined' && module.exports) {
  module.exports = Buybox2CartHandler;
}
